import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { User } from '@shared/schema';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refetchUser: () => Promise<void>;
  clearUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState<number>(0);

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchUser = async (force = false) => {
    const now = Date.now();
    
    // If we have cached data and it's not expired, don't fetch
    if (!force && user && (now - lastFetch) < CACHE_DURATION) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch('/api/auth/user', {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setLastFetch(now);
      } else if (response.status === 401) {
        // User not authenticated
        setUser(null);
        setLastFetch(now);
      } else {
        console.error('Error fetching user:', response.status, response.statusText);
        // Don't clear user on other errors, keep cached data
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // Don't clear user on network errors, keep cached data
    } finally {
      setIsLoading(false);
    }
  };

  const refetchUser = async () => {
    await fetchUser(true);
  };

  const clearUser = () => {
    setUser(null);
    setLastFetch(0);
  };

  // Initial fetch
  useEffect(() => {
    fetchUser();
  }, []);

  // Periodic refresh (30 min) — Stripe sync is cached server-side for 5 min
  useEffect(() => {
    const interval = setInterval(() => {
      if (user) {
        fetchUser();
      }
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(interval);
  }, [user]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    refetchUser,
    clearUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
